import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { YYYY_MM_DD } from "constants/date";
import { Field, FieldProps, useFormikContext } from "formik";
import { CRM_FIELD_TYPE, CrmField } from "types/Models/crmFields";
import { crmFieldFormKey } from "./crmFormUtils";

/**
 * Editable control for one CRM field. The formik field name is normally the field uuid, so the
 * parent form can diff its values straight back into the {field_id, value} payload the API
 * takes - but for a field inside a repeatable section's instance, field.uuid alone would collide
 * across instances (they share the same field definitions), so crmFieldFormKey prefixes it with
 * the instance id in that case.
 */
export const CRMFieldControl = ({ field }: { field: CrmField }) => {
  const { values, setFieldValue } = useFormikContext<Record<string, any>>();
  const name = crmFieldFormKey(field);
  const options = field.options.map((option) => ({
    label: option.label,
    value: option.value,
  }));

  switch (field.field_type) {
    case CRM_FIELD_TYPE.LONG_TEXT:
      return <TextArea name={name} label="" />;

    case CRM_FIELD_TYPE.NUMBER:
    case CRM_FIELD_TYPE.CURRENCY:
      return (
        <TextInput
          name={name}
          label=""
          type="number"
          customPrefix={
            field.field_type === CRM_FIELD_TYPE.CURRENCY ? (
              <span>$</span>
            ) : undefined
          }
        />
      );

    case CRM_FIELD_TYPE.DATE:
      return (
        <Field name={name}>
          {({ field: formikField }: FieldProps) => (
            <DateInput
              {...formikField}
              label=""
              placeholder="Select date"
              saveFormat={YYYY_MM_DD}
              portalId={name}
            />
          )}
        </Field>
      );

    case CRM_FIELD_TYPE.CHECKBOX:
      return (
        <Checkbox
          name={name}
          checked={Boolean(values[name])}
          onChange={(event) => setFieldValue(name, event.target.checked)}
        />
      );

    case CRM_FIELD_TYPE.SINGLE_SELECT: {
      // A value saved under an option that's since been removed from the list won't be in
      // `options` any more - show it as-is rather than blanking it out, but since it's not a
      // real option, re-saving the field forces picking one of the current options instead.
      const currentValue = values[name];
      const selectedOption = options.find(
        (option) => option.value === currentValue
      );
      const value = currentValue
        ? selectedOption || { label: currentValue, value: currentValue }
        : null;

      return (
        <Combobox
          withForm
          name={name}
          label=""
          menuPortalTarget={document.body}
          creatable={false}
          isClearable
          options={options}
          value={value}
        />
      );
    }

    case CRM_FIELD_TYPE.MULTI_SELECT:
      return (
        <Combobox
          withForm
          isMulti
          name={name}
          label=""
          menuPortalTarget={document.body}
          creatable={false}
          isClearable
          options={options}
          value={options.filter((option) =>
            ((values[name] as string[]) || []).includes(option.value)
          )}
        />
      );

    default:
      return <TextInput name={name} label="" />;
  }
};
